"use client";

/**
 * MIN-293 — the compound's own context. Relocated from
 * `apps/provider/src/components/agent-chat/context.ts` (+ its sibling
 * `use-agent-chat-context.ts`) now that the connected parts live in the
 * package; same shape, plus `meta.renderers` — the `PartRenderers` registry
 * `Root`'s optional `renderers` prop merges over the house defaults (see the
 * issue's "prop surface — pinned" section), reaching `Turn` through this
 * context rather than a prop on `Turn` itself or a second provider.
 */
import { createContext, type RefObject, use } from "react";
import type { ChatBubble } from "../elements/chat-turn";
import type { PartRenderers } from "../elements/render-turn-part";

export type { ChatBubble, SourceRef, ToolActivity } from "../elements/chat-turn";

/** What `PromptInput`'s submit path folds into the *next* thread's creation
 * body when an `AgentChat.Settings` provider is above it (MIN-294). Ignored
 * once a thread already exists — model choice only takes effect on the
 * new-chat page (the audit's "selectorsLocked, then discarded" note);
 * `submit()` with no argument is unaffected either way. */
export interface AgentChatSubmitOverrides {
  model?: string;
}

export interface AgentChatContextValue {
  state: {
    messages: ChatBubble[];
    draft: string;
    canSubmit: boolean;
    isStreaming: boolean;
    /** false when no thread is selected — useful for disabling thread-scoped actions. */
    hasActiveThread: boolean;
    /** The thread on screen — `Root`'s `threadId` prop, verbatim. */
    activeThreadId: string | null;
    /** Pinned subject patient (FHIR Patient.id) of the active thread, or null. */
    subjectPatientId: string | null;
  };
  actions: {
    setDraft: (v: string) => void;
    submit: (overrides?: AgentChatSubmitOverrides) => void;
    /** Ends the in-flight reply (`useChat`'s own `stop`), keeping whatever
     * text already streamed. A no-op when nothing is streaming. */
    stop: () => void;
    newChat: () => void;
    deleteChat: () => void;
  };
  meta: {
    inputRef: RefObject<HTMLTextAreaElement | null>;
    /** The merged registry — house defaults with any of `Root`'s `renderers`
     * overrides applied. */
    renderers: PartRenderers;
  };
}

export const AgentChatContext = createContext<AgentChatContextValue | null>(null);

export function useAgentChatContext(): AgentChatContextValue {
  const ctx = use(AgentChatContext);
  if (!ctx) {
    throw new Error("AgentChat.* components must be rendered inside <AgentChat.Root>");
  }
  return ctx;
}
